import type { Editor, Node } from "@tiptap/core";
import { NodeSelection, TextSelection } from "prosemirror-state";
import { eventBus } from "@/services-frontend/comment/eventBus";
import { extractNoteId } from "@/utils/noteUrl";

/**
 * Operations on whichever block the caret is in — shared by the drag-handle
 * menu and the keyboard shortcuts.
 *
 * Kept out of menu-config so both callers can reach them: menu-config imports
 * the shortcut registry to render its hints, so a binding importing menu-config
 * would close a cycle.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyNode = any;

const LIST_ITEM_TYPES = new Set(["listItem", "taskItem"]);
const NON_TARGET_TYPES = new Set(["doc", "columnLayout", "columnItem"]);

/**
 * The block a whole-block action should operate on, resolved from the caret.
 *
 * Promotes to the enclosing list item, which is what the drag handle targets —
 * it explicitly backs out of the paragraph to the item before acting. Without
 * that step a keyboard caller sees the paragraph *inside* the item instead, and
 * duplicating it yields a copy with no bullet, number or checkbox.
 *
 * Both listItem and taskItem carry blockIds, so the promoted node is still a
 * valid target for a block link.
 */
export function resolveBlockTarget(
  editor: Editor,
): { node: AnyNode; position: number } | null {
  const { selection } = editor.state;
  const sel = selection as AnyNode;

  // Drag-handle click: the node is already selected directly.
  if (sel?.node && sel.node.type?.isBlock) {
    return { node: sel.node, position: selection.from };
  }

  const $from = selection.$from;

  for (let depth = $from.depth; depth > 0; depth--) {
    if (LIST_ITEM_TYPES.has($from.node(depth).type.name)) {
      return { node: $from.node(depth), position: $from.before(depth) };
    }
  }

  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.isBlock && !NON_TARGET_TYPES.has(node.type.name)) {
      return { node, position: $from.before(depth) };
    }
  }
  return null;
}

/**
 * Copy a link that reopens the page focused on this block.
 *
 * Addressed by the block's stable blockId rather than its position, which
 * shifts on every edit. BlockLinkFocusExtension resolves the hash on load.
 *
 * Returns false when the block has no id yet, so a caller can report it.
 */
export async function copyLinkToBlock(node: Node): Promise<boolean> {
  const blockId = (node as AnyNode).attrs?.blockId;
  if (!blockId) return false;

  const url = new URL(window.location.href);
  url.hash = `block-${blockId}`;
  try {
    await navigator.clipboard.writeText(url.toString());
    return true;
  } catch {
    return false;
  }
}

/**
 * Ask a page atom to duplicate the page it links to. Handled by the page
 * NodeView, which has the workspace/block context this module cannot reach.
 */
export const PAGE_BLOCK_DUPLICATE_EVENT = "page-block-duplicate";

/** Page ids live in the atom's href, which may be slugged (/notes/{slug}-{id}). */
function extractPageId(href?: string | null): string | null {
  if (!href) return null;
  return extractNoteId(href.split("/").pop() ?? "") || null;
}

/** Insert a copy of the block directly after itself. */
export function duplicateBlock(editor: Editor, node: Node, pos: number): void {
  // A page atom is only a link: copying the node would point the copy at the same
  // page, so the NodeView clones the page itself instead.
  if ((node as AnyNode).type?.name === "page") {
    if (!editor.isEditable) return;
    const blockId = (node as AnyNode).attrs?.blockId ?? extractPageId((node as AnyNode).attrs?.href);
    // editor + pos identify the ONE atom acted on — every mounted page atom
    // listens for this event.
    if (blockId) eventBus.emit(PAGE_BLOCK_DUPLICATE_EVENT, { blockId, editor, pos });
    return;
  }

  const tr = editor.state.tr;
  const nodeSize = (node as AnyNode).nodeSize || 1;
  const slice = editor.state.doc.slice(pos, pos + nodeSize);
  tr.insert(pos + nodeSize, slice.content);
  editor.view.dispatch(tr);
}

/**
 * Select the block the caret is in, so it can be moved, copied or deleted as a
 * unit. Pressing again with a block already selected drops back to a caret.
 *
 * A NodeSelection is what the block-selection plugin already draws its overlay
 * for, so this reuses the highlight the drag handle produces rather than
 * introducing a second notion of "selected".
 */
export function toggleBlockSelection(editor: Editor): boolean {
  const { state } = editor;

  if (state.selection instanceof NodeSelection) {
    // Back to editing: drop the caret inside the block that was selected.
    const inside = TextSelection.near(state.doc.resolve(state.selection.from + 1));
    editor.view.dispatch(state.tr.setSelection(inside));
    return true;
  }

  const target = resolveBlockTarget(editor);
  if (!target) return false;

  try {
    const selection = NodeSelection.create(state.doc, target.position);
    editor.view.dispatch(state.tr.setSelection(selection));
    return true;
  } catch {
    // Not a selectable position (an inline or atom boundary) — leave the
    // caret alone rather than throwing mid-keystroke.
    return false;
  }
}

/**
 * Move the current block above its previous sibling, or below its next one.
 *
 * Reinserts the very same node, so its blockId travels with it and comments and
 * block links stay anchored — the same reason a drag-and-drop move keeps them.
 *
 * Returns false at the ends of a parent, which leaves the keystroke its default
 * rather than consuming it silently.
 */
export function moveBlock(editor: Editor, direction: "up" | "down"): boolean {
  const { state } = editor;
  const target = resolveBlockTarget(editor);
  if (!target) return false;

  const $pos = state.doc.resolve(target.position);
  const parent = $pos.parent;
  const index = $pos.index();

  const siblingIndex = direction === "up" ? index - 1 : index + 1;
  if (siblingIndex < 0 || siblingIndex >= parent.childCount) return false;

  const node = parent.child(index);
  const sibling = parent.child(siblingIndex);

  const from = target.position;
  const to = from + node.nodeSize;
  // Where the node should land: the sibling's start when moving up, its end
  // when moving down.
  const destination = direction === "up" ? from - sibling.nodeSize : to + sibling.nodeSize;

  // Keep the caret where it was relative to the block's start.
  const caretOffset = state.selection.from - from;
  const wasNodeSelection = state.selection instanceof NodeSelection;

  const tr = state.tr;
  tr.delete(from, to);
  // Map through the deletion rather than adjusting by hand — moving down
  // shifts everything after the removed range.
  const insertAt = tr.mapping.map(destination);
  tr.insert(insertAt, node);

  // `near` rather than `create`: the offset can land on a block boundary — the
  // start of a list item holds blocks, not inline content — and `near` searches
  // outward for the closest usable position instead of rejecting it. Clamped so
  // a block moved to the end cannot resolve past the document.
  const desired = Math.min(insertAt + Math.max(1, caretOffset), tr.doc.content.size);
  try {
    tr.setSelection(
      wasNodeSelection
        ? NodeSelection.create(tr.doc, insertAt)
        : TextSelection.near(tr.doc.resolve(desired)),
    );
  } catch {
    // Only NodeSelection.create can refuse (a non-selectable node). Restoring
    // the selection is a nicety; the move itself has already succeeded.
  }

  editor.view.dispatch(tr.scrollIntoView());
  return true;
}
